import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { SettlementSummaryBar } from "@/components/pages/settlement/components/SettlementSummaryBar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UsersTab } from "./components/UsersTab";
import { GroupsTab } from "./components/GroupsTab";
import { RolesTab } from "./components/RolesTab";
import { resolveUsersPageUiConfig, UsersTabKey } from "./usersPageConfig";
import { PageState } from "@/components/common/PageState";
import {
  hasKnownPlatformPermissions,
  hasPlatformPermission,
  hasTenantPermission,
  isGuesteriaAdminRole,
  isSuperAdminRole,
  isTenantAdminRole,
} from "@/utils/accessControl";

const servicesTabsListClassName =
  "h-9 w-[685px] items-stretch rounded-lg bg-[#FBFAFF] py-1.5 px-1 gap-1 text-foreground shadow-sm";

const servicesTabsTriggerClassName =
  "flex flex-1 items-center justify-center self-stretch rounded-md border border-transparent bg-transparent px-2 py-1 text-xs font-medium leading-none text-[#0A0A0A] shadow-none transition-colors hover:bg-[#EDE9FE]/40 data-[state=active]:border-[#C7BCFF] data-[state=active]:bg-[#F2F0FF] data-[state=active]:shadow-none";

const parseUsersTab = (value: string | null): UsersTabKey | null =>
  Object.values(UsersTabKey).find((tab) => tab === value) ?? null;

export const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuthenticatedUser();
  const effectiveRoleKey =
    userProfile?.effectiveRole?.baseRole ?? userProfile?.role;
  const uiConfig = useMemo(
    () => resolveUsersPageUiConfig(effectiveRoleKey),
    [effectiveRoleKey],
  );
  const effectiveUiConfig = useMemo(() => {
    const isPlatformAdmin =
      isSuperAdminRole(userProfile) || isGuesteriaAdminRole(userProfile);
    const hasCustomPlatformRole =
      isPlatformAdmin && hasKnownPlatformPermissions(userProfile);

    if (hasCustomPlatformRole) {
      const canReadUsers = hasPlatformPermission(
        userProfile,
        "platform:users:read",
      );
      const canAdminUsers = hasPlatformPermission(
        userProfile,
        "platform:users:administrate",
      );

      return {
        ...uiConfig,
        allowedTabs: canReadUsers
          ? [UsersTabKey.USERS, UsersTabKey.GROUPS, UsersTabKey.ROLES]
          : [],
        canAddUser: canAdminUsers,
        canAddGroup: canAdminUsers,
        canAddCustomRole: canAdminUsers,
        canOpenUserDetails: canAdminUsers,
        // "View" (read-only) roles can open detail/preview screens.
        canViewUserDetails: canReadUsers,
        canViewGroupDetails: canReadUsers,
        canDeleteUser: canAdminUsers,
      };
    }

    if (
      isTenantAdminRole(userProfile) &&
      userProfile?.effectiveRole?.type === "CUSTOM"
    ) {
      const canAdmin = hasTenantPermission(userProfile, "users:administrate");
      const canRead =
        canAdmin || hasTenantPermission(userProfile, "users:read");
      return {
        ...uiConfig,
        canAddUser: canAdmin,
        canAddGroup: canAdmin,
        canAddCustomRole: canAdmin,
        canOpenUserDetails: canAdmin,
        // "View" (read-only) roles can open detail/preview screens.
        canViewUserDetails: canRead,
        canViewGroupDetails: canRead,
        canDeleteUser: canAdmin,
      };
    }

    return uiConfig;
  }, [uiConfig, userProfile]);
  const tenantId = searchParams.get("tenantId") || undefined;
  const backTo = searchParams.get("backTo");
  const availableTabs = effectiveUiConfig.allowedTabs;
  const requestedTab = parseUsersTab(searchParams.get("tab"));
  const activeTab =
    requestedTab && availableTabs.includes(requestedTab)
      ? requestedTab
      : (availableTabs[0] ?? UsersTabKey.USERS);

  const handleTabChange = (value: string) => {
    const nextTab = parseUsersTab(value);
    if (!nextTab || !availableTabs.includes(nextTab)) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("tab", nextTab);
    setSearchParams(nextSearchParams);
  };

  if (availableTabs.length === 0) {
    return (
      <div className="mx-auto flex max-w-[1344px] flex-col p-12">
        <PageState
          tone="error"
          text={t(
            "common.insufficientPlatformPermissions",
            "Insufficient platform permissions.",
          )}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col space-y-6 p-12">
      <SettlementSummaryBar />
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full space-y-4"
      >
        <div className="flex items-center justify-center">
          <TabsList className={servicesTabsListClassName}>
            {availableTabs.includes(UsersTabKey.USERS) ? (
              <TabsTrigger
                value={UsersTabKey.USERS}
                className={servicesTabsTriggerClassName}
              >
                {t("users.page.tabs.users")}
              </TabsTrigger>
            ) : null}
            {availableTabs.includes(UsersTabKey.GROUPS) ? (
              <TabsTrigger
                value={UsersTabKey.GROUPS}
                className={servicesTabsTriggerClassName}
              >
                {t("users.page.tabs.groups")}
              </TabsTrigger>
            ) : null}
            {availableTabs.includes(UsersTabKey.ROLES) ? (
              <TabsTrigger
                value={UsersTabKey.ROLES}
                className={servicesTabsTriggerClassName}
              >
                {t("users.page.tabs.roles")}
              </TabsTrigger>
            ) : null}
          </TabsList>
        </div>

        {availableTabs.includes(UsersTabKey.USERS) ? (
          <TabsContent value={UsersTabKey.USERS} className="mt-0">
            <UsersTab
              backTo={backTo}
              tenantId={tenantId}
              canAddUser={effectiveUiConfig.canAddUser}
              canOpenUserDetails={effectiveUiConfig.canOpenUserDetails}
              canViewUserDetails={effectiveUiConfig.canViewUserDetails}
              canImpersonateUsers={isTenantAdminRole(userProfile)}
              canEditGuesteriaAdmin={effectiveUiConfig.canEditGuesteriaAdmin}
              canDeleteUser={effectiveUiConfig.canDeleteUser}
              canAdministrateUsers={
                isTenantAdminRole(userProfile)
                  ? effectiveUiConfig.canAddUser
                  : hasPlatformPermission(
                      userProfile,
                      "platform:users:administrate",
                    )
              }
              showTenantColumn={effectiveUiConfig.showTenantColumn}
              possibleRoles={effectiveUiConfig.possibleRoles}
              userListSource={effectiveUiConfig.userListSource}
            />
          </TabsContent>
        ) : null}
        {availableTabs.includes(UsersTabKey.GROUPS) ? (
          <TabsContent value={UsersTabKey.GROUPS} className="mt-0">
            <GroupsTab
              backTo={backTo}
              canAddGroup={effectiveUiConfig.canAddGroup}
              canEditGroup={effectiveUiConfig.canAddGroup}
              canViewGroup={effectiveUiConfig.canViewGroupDetails}
              scope={effectiveUiConfig.accessGroupScope}
            />
          </TabsContent>
        ) : null}
        {availableTabs.includes(UsersTabKey.ROLES) ? (
          <TabsContent value={UsersTabKey.ROLES} className="mt-0">
            <RolesTab
              backTo={backTo}
              canAddCustomRole={effectiveUiConfig.canAddCustomRole}
              scope={effectiveUiConfig.customRoleScope}
            />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
};
